import { ChangeEvent, useState } from "react";
import Button from "rsuite/Button";
import Loader from "rsuite/Loader";
import Message from "rsuite/Message";
import { FiUploadCloud } from "react-icons/fi";
import {
  OrderListImportError,
  useOrderListMasterData,
  useUploadOrderListMasterData,
} from "./hooks/useOrderListMasterData";
import { ORDER_LIST_DATASETS, OrderListDatasetDefinition } from "./orderListDataConfig";
import "./OrderListData.css";

const getImportErrors = (error: OrderListImportError | null): string[] => {
  if (!error) {
    return [];
  }
  const response = error.response?.data;
  if (response && "errors" in response && Array.isArray(response.errors)) {
    return response.errors;
  }
  return [error.message || "The file could not be uploaded."];
};

const DatasetPanel = <T,>({ definition }: { definition: OrderListDatasetDefinition<T> }): JSX.Element => {
  const [file, setFile] = useState<File | null>(null);
  const query = useOrderListMasterData<T>(definition);
  const upload = useUploadOrderListMasterData(definition);
  const errors = getImportErrors(upload.error);

  const selectFile = (event: ChangeEvent<HTMLInputElement>): void => {
    setFile(event.target.files?.[0] ?? null);
    upload.reset();
  };

  const submit = (): void => {
    if (file) {
      upload.mutate(file, { onSuccess: () => setFile(null) });
    }
  };

  return (
    <section className="order-list-dataset" aria-labelledby={`${definition.queryKey}-title`}>
      <div className="order-list-dataset__heading">
        <div>
          <h3 id={`${definition.queryKey}-title`}>{definition.label}</h3>
          <p>{definition.description}</p>
        </div>
        <span className="order-list-dataset__count">
          {query.isLoading ? <Loader size="xs" /> : `${query.data?.records.length ?? 0} records`}
        </span>
      </div>

      <div className="order-list-dataset__audit">
        {query.data?.upload ? (
          <>
            <strong>v{query.data.upload.version}</strong>
            <span>{query.data.upload.fileName}</span>
            <span>{new Date(query.data.upload.uploadedAtUtc).toLocaleString()}</span>
            <span>{query.data.upload.uploadedBy}</span>
          </>
        ) : (
          <span>No file published</span>
        )}
      </div>

      <label className="order-list-dataset__file">
        <span>{file?.name ?? definition.expectedFileName}</span>
        <input type="file" accept="application/json,.json" onChange={selectFile} />
        <span className="order-list-dataset__browse">Choose file</span>
      </label>

      {query.isError && <Message type="error" showIcon>Could not load the active dataset.</Message>}
      {errors.length > 0 && (
        <Message type="error" showIcon>
          <ul>{errors.map((error) => <li key={error}>{error}</li>)}</ul>
        </Message>
      )}
      {upload.isSuccess && <Message type="success" showIcon>Published successfully.</Message>}

      <Button appearance="primary" disabled={!file || upload.isLoading} loading={upload.isLoading} onClick={submit}>
        <FiUploadCloud aria-hidden="true" />
        Publish JSON
      </Button>
    </section>
  );
};

const OrderListData = (): JSX.Element => (
  <div className="order-list-data">
    <DatasetPanel definition={ORDER_LIST_DATASETS.ingredients} />
    <DatasetPanel definition={ORDER_LIST_DATASETS.recipes} />
    <DatasetPanel definition={ORDER_LIST_DATASETS.ingredientRecipes} />
  </div>
);

export default OrderListData;